const WALLET_ORDER = ['apple_pay', 'paypal', 'shop_pay', 'google_pay']
const HOST_TAG = 'shopify-accelerated-checkout-cart'
const CONFIG_ATTR = 'wallet-configs'
const FULL_CONFIG_ATTR = 'data-wallet-configs-full'
const SLOT_PREFIX = 'button-'
const SETTLE_DELAY = 300
const BUTTON_MIN_WIDTH = 100
const BUTTON_GAP = 10
const ineligibleWallets = new Set()

function parseConfigs(raw) {
  try {
    const configs = JSON.parse(raw)
    return Array.isArray(configs) ? configs : null
  } catch (error) {
    console.warn('[express-checkout-order] invalid wallet-configs, keeping Shopify\'s order:', error)
    return null
  }
}

const normalizeName = name => String(name ?? '').toLowerCase().replace(/[^a-z0-9]/g, '')

function walletsThatFit(width) {
  if (!width || width <= 0) return Infinity
  const count = Math.ceil((width + BUTTON_GAP) / (BUTTON_MIN_WIDTH + BUTTON_GAP)) - 1
  return Math.max(1, count)
}

function buildConfigs(full, width) {
  const rank = wallet => {
    const index = WALLET_ORDER.indexOf(wallet?.name)
    return index === -1 ? WALLET_ORDER.length : index
  }
  return full
    .filter(wallet => !ineligibleWallets.has(normalizeName(wallet?.name)))
    .sort((a, b) => rank(a) - rank(b))
    .slice(0, walletsThatFit(width))
}

const renderedWallets = el => [...el.children]
  .map(child => child.getAttribute('slot') || '')
  .filter(slot => slot.startsWith(SLOT_PREFIX))
  .map(slot => normalizeName(slot.slice(SLOT_PREFIX.length)))

function detectIneligible(container) {
  const el = container.querySelector(HOST_TAG)
  if (!el) return false

  const rendered = renderedWallets(el)
  if (!rendered.length) return false

  let found = false
  for (const wallet of parseConfigs(el.getAttribute(CONFIG_ATTR)) ?? []) {
    const name = normalizeName(wallet?.name)
    if (name && !rendered.includes(name) && !ineligibleWallets.has(name)) {
      ineligibleWallets.add(name)
      found = true
    }
  }
  return found
}

function applyOrder(container) {
  const el = container.querySelector(HOST_TAG)
  if (!el) return

  let fullRaw = el.getAttribute(FULL_CONFIG_ATTR)
  if (!fullRaw) {
    fullRaw = el.getAttribute(CONFIG_ATTR)
    if (!fullRaw) return
    el.setAttribute(FULL_CONFIG_ATTR, fullRaw)
  }

  const full = parseConfigs(fullRaw)
  if (!full) return

  const desiredConfigs = buildConfigs(full, container.clientWidth)
  const desired = JSON.stringify(desiredConfigs)
  if (desired === el.getAttribute(CONFIG_ATTR)) return

  const rendered = renderedWallets(el)
  const desiredNames = desiredConfigs.map(wallet => normalizeName(wallet?.name))
  if (rendered.length && rendered.join(',') === desiredNames.join(',')) return

  if (!window.customElements?.get(HOST_TAG) && !el.children.length) {
    el.setAttribute(CONFIG_ATTR, desired)
    return
  }

  const clone = document.createElement(HOST_TAG)
  for (const { name, value } of el.attributes) {
    clone.setAttribute(name, name === CONFIG_ATTR ? desired : value)
  }
  el.replaceWith(clone)
}

export function reorderExpressCheckoutWallets(container) {
  if (!container || typeof MutationObserver === 'undefined') return () => {}

  applyOrder(container)

  let settleTimer = null
  const scheduleDetection = () => {
    clearTimeout(settleTimer)
    settleTimer = setTimeout(() => {
      settleTimer = null
      if (detectIneligible(container)) applyOrder(container)
    }, SETTLE_DELAY)
  }

  const mutationObserver = new MutationObserver(() => {
    applyOrder(container)
    scheduleDetection()
  })
  mutationObserver.observe(container, { childList: true, subtree: true })

  let lastWidth = container.clientWidth
  const onResize = () => {
    const width = container.clientWidth
    if (width === lastWidth) return
    lastWidth = width
    applyOrder(container)
  }
  const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(onResize)
  resizeObserver?.observe(container)
  window.addEventListener('resize', onResize)

  return () => {
    mutationObserver.disconnect()
    resizeObserver?.disconnect()
    window.removeEventListener('resize', onResize)
    clearTimeout(settleTimer)
  }
}
